import { useEffect } from 'react';
import {
  ApplyToAllIcon,
  ApplyToNextIcon,
  Checkbox,
  EllipsisMenu,
  Input,
  PrimaryButton,
  SecondaryButton,
  Typography,
} from '@weareopenr/component-library';
import { useForm } from 'react-hook-form';
import { useCreateMealtime } from 'src/hooks/useCreateMealtime';
import { DAYS } from 'src/constants';
import { capitalise } from 'src/helpers/capitalise';
import { createInitValues } from '../utilities/createInitValues';
import { doSchedulesOverlap } from '../utilities/doSchedulesOverlap';
import { TDay } from 'src/api/getMenu/types';
import { IFormValues, IMealtimeDailyValues, IMealtimeView, TMealtimeValues } from '../types';
import { IMealtime } from 'src/types/mealtimes.types';
import styles from './MealtimeView.module.scss';
import { useUpdateMealtime } from '../../../hooks/useUpdateMealtime/useUpdateMealtime';

const MealtimeView = ({ menuId, onModalClose, mealtimes, onSuccess, setMealtimeId, type, mealtime }: IMealtimeView) => {
  const { mutate: createMealtime, isLoading: isCreating } = useCreateMealtime(menuId, setMealtimeId, onSuccess);
  const { mutate: updateMealtime, isLoading: isUpdating } = useUpdateMealtime(menuId, mealtime?.mealtime_id, onSuccess);

  const formFields = useForm<IFormValues>({
    defaultValues: createInitValues(mealtime),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    register,
    watch,
    setError,
    clearErrors,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = formFields;

  const formMealtimeValues: TMealtimeValues = watch('schedules');
  const hasCheckedValues: boolean = Object.values(formMealtimeValues).some(
    (values: IMealtimeDailyValues) => values.enabled,
  );

  useEffect(() => {
    hasCheckedValues
      ? clearErrors('selectedFields')
      : setError('selectedFields', { type: 'custom', message: 'You must select at least one day of the week' });
  }, [hasCheckedValues]);

  const onSubmit = async ({ name, schedules }: IFormValues) => {
    const requestBody = {
      name,
      schedules: Object.entries(schedules).map(
        ([day_of_week, { start_time, end_time, enabled }]: [TDay, IMealtimeDailyValues]) => ({
          day_of_week,
          start_time,
          end_time,
          enabled,
        }),
      ),
    };
    if (type === 'edit') {
      updateMealtime(requestBody);
    } else {
      createMealtime(requestBody);
    }
  };

  const handleCopyValues = (currentDay: TDay, action: 'all' | 'next') => {
    const targetDays = action === 'all' ? DAYS : [DAYS[DAYS.indexOf(currentDay) + 1]];

    targetDays.forEach((day: TDay) => {
      setValue(`schedules.${day}.start_time`, watch(`schedules.${currentDay}.start_time`), { shouldValidate: true });
      setValue(`schedules.${day}.end_time`, watch(`schedules.${currentDay}.end_time`), { shouldValidate: true });
      setValue(`schedules.${day}.enabled`, watch(`schedules.${currentDay}.enabled`), { shouldValidate: true });
    });
  };

  return (
    <form
      className={styles['mealtime-view']}
      onSubmit={handleSubmit(onSubmit)}
      id="mealtime-view"
      aria-label="mealtime-view"
    >
      <Typography size="xxlarge" weight="semi-bold">
        {type === 'create' ? ' Create' : 'Edit'} your mealtime
      </Typography>
      <Input
        type="text"
        label="Mealtime name"
        register={register('name', {
          setValueAs: (v: string) => v.trim(),
          required: 'Name is required',
          maxLength: {
            value: 40,
            message: 'Max 40 characters',
          },
          validate: {
            nameIsUnique: (name: string) =>
              !mealtimes.some((mealtime: IMealtime) => mealtime.name.toLowerCase() === name.toLowerCase()) ||
              'A mealtime with this name already exists',
          },
        })}
        error={errors?.name?.message}
      />
      <div className={styles['mealtime-times']}>
        {DAYS.map((day: TDay) => (
          <div className={styles['field-container']} key={day}>
            <div
              className={styles['field-checkbox']}
              onClick={() => {
                trigger([`schedules.${day}.start_time`, `schedules.${day}.end_time`]);
              }}
            >
              <Checkbox label={capitalise(day)} name={`schedules.${day}.enabled`} register={register} />
            </div>
            <div className={styles['field-controls']}>
              <div className={styles['field-inputs']}>
                <Input
                  type="time"
                  label="Start time"
                  register={register(`schedules.${day}.start_time`, {
                    validate: {
                      hasValue: (startTime: string, { schedules }: IFormValues) =>
                        !schedules?.[day]?.enabled || !!startTime || 'Start time is empty',
                      isBeforeEndTime: (startTime: string, { schedules }: IFormValues) =>
                        startTime < schedules?.[day].end_time || 'Later than/Same as End time',
                      isNotOverlap: (startTime: string, { schedules }: IFormValues) =>
                        doSchedulesOverlap(
                          startTime,
                          schedules?.[day].end_time,
                          schedules?.[day]?.enabled,
                          day,
                          mealtimes,
                        )
                          ? 'Overlaps other mealtimes'
                          : true,
                    },
                  })}
                  data-testid={`schedules.${day}.start_time`}
                  disabled={!watch(`schedules.${day}.enabled`)}
                  error={errors?.schedules?.[day]?.start_time?.message}
                />
                <div className={styles['input-divider']} />
                <Input
                  type="time"
                  label="End time"
                  register={register(`schedules.${day}.end_time`, {
                    validate: {
                      hasValue: (endTime: string, { schedules }: IFormValues) =>
                        !schedules?.[day].enabled || !!endTime || 'End time is empty',
                      isNotOverlap: (endTime: string, { schedules }: IFormValues) =>
                        doSchedulesOverlap(
                          schedules?.[day].start_time,
                          endTime,
                          schedules?.[day]?.enabled,
                          day,
                          mealtimes,
                        )
                          ? 'Overlaps other mealtimes'
                          : true,
                    },
                    deps: [`schedules.${day}.start_time`],
                  })}
                  data-testid={`schedules.${day}.end_time`}
                  disabled={!watch(`schedules.${day}.enabled`)}
                  error={errors?.schedules?.[day]?.end_time?.message}
                />
              </div>
              <div className={styles['ellipsis-menu']}>
                <EllipsisMenu
                  variant="ghost"
                  options={
                    DAYS.indexOf(day) !== DAYS.length - 1
                      ? [
                          {
                            id: 1,
                            name: 'Apply to next',
                            icon: ApplyToNextIcon,
                            onOptionClick: () => handleCopyValues(day, 'next'),
                          },
                          {
                            id: 0,
                            name: 'Apply to all',
                            icon: ApplyToAllIcon,
                            onOptionClick: () => handleCopyValues(day, 'all'),
                          },
                        ]
                      : [
                          {
                            id: 0,
                            name: 'Apply to all',
                            icon: ApplyToAllIcon,
                            onOptionClick: () => handleCopyValues(day, 'all'),
                          },
                        ]
                  }
                  disabled={
                    !!errors?.schedules?.[day]?.start_time?.message || !!errors?.schedules?.[day]?.end_time?.message
                  }
                />
              </div>
            </div>
          </div>
        ))}
        {errors.selectedFields && (
          <div className={styles['error-container']}>
            <Typography>{errors?.selectedFields?.message}</Typography>
          </div>
        )}
      </div>
      <div className={styles['buttons-container']}>
        <SecondaryButton size="full-width" buttonStyle="outline" type="button" onClick={onModalClose}>
          Back
        </SecondaryButton>
        <PrimaryButton loading={isSubmitting || isCreating || isUpdating} size="full-width" type="submit">
          Save{type === 'create' && ' & Continue'}
        </PrimaryButton>
      </div>
    </form>
  );
};

export default MealtimeView;
